import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Textarea } from '../../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Loader2, Save, Upload } from 'lucide-react';

import { invPhoto, checkPhoto, uploadInventoryPhoto } from './photos';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const CONDITIONS = ['Excellent', 'Good', 'Fair', 'Poor'];
const STATUSES = ['Available', 'In Use', 'Needs Repair', 'Retired'];


const empty = {
  name: '', photo_url: '', vendor_name: '', purchase_url: '', store_address: '',
  purchase_price: '', current_replacement_cost: '', date_purchased: '',
  current_location: '', condition: 'Good', status: 'Available', notes: '',
};

const AddEquipmentDialog = ({ open, onOpenChange, headers, equipment = null, onSaved }) => {
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [photoFile, setPhotoFile] = useState(null);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  useEffect(() => { if (open) setPhotoFile(null);
    if (open) setForm(equipment ? { ...empty, ...equipment } : empty); }, [open, equipment]);

  const onPhoto = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const err = checkPhoto(f);
    if (err) { toast.error(err); return; }
    setPhotoFile(f);
    set('photo_url', URL.createObjectURL(f));
  };

  const save = async () => {
    if (!form.name) { toast.error('Name is required'); return; }
    setBusy(true);
    try {
      const { photo_url: _photo, ...payload } = form;
      ['purchase_price', 'current_replacement_cost'].forEach(k => {
        if (payload[k] === '' || payload[k] == null) delete payload[k];
        else payload[k] = parseFloat(payload[k]);
      });
      Object.keys(payload).forEach(k => { if (payload[k] === '') delete payload[k]; });
      if (equipment?.id) {
        await axios.patch(`${API_URL}/api/admin/inventory/equipment/${equipment.id}`, payload, { headers });
        if (photoFile) await uploadInventoryPhoto('equipment', equipment.id, photoFile, headers);
        toast.success('Equipment updated');
      } else {
        const created = await axios.post(`${API_URL}/api/admin/inventory/equipment`, payload, { headers });
        if (photoFile) await uploadInventoryPhoto('equipment', created.data.id, photoFile, headers);
        toast.success('Equipment added');
      }
      onSaved(); onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="add-equipment-dialog">
        <DialogHeader><DialogTitle>{equipment ? 'Edit equipment' : 'Add equipment'}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <F label="Equipment Name *"><Input value={form.name} onChange={e => set('name', e.target.value)} data-testid="eq-name" /></F>
            <F label="Photo (≤2 MB)">
              <div className="flex items-center gap-2">
                {form.photo_url && <img src={invPhoto(form.photo_url)} alt="" className="w-16 h-16 rounded bg-[#f4f2ed] object-contain" />}
                <label className="flex-1 inline-flex items-center justify-center gap-1 border border-dashed border-zinc-300 rounded-lg py-2 text-xs cursor-pointer">
                  <Upload className="w-3 h-3" /> {form.photo_url ? 'Change' : 'Upload'}
                  <input type="file" accept="image/jpeg,image/png,image/webp" onChange={onPhoto} className="hidden" data-testid="eq-photo" />
                </label>
              </div>
            </F>
            <F label="Vendor / Store"><Input value={form.vendor_name} onChange={e => set('vendor_name', e.target.value)} /></F>
            <F label="Purchase URL"><Input value={form.purchase_url} onChange={e => set('purchase_url', e.target.value)} /></F>
            <F label="Physical Store Address"><Input value={form.store_address} onChange={e => set('store_address', e.target.value)} /></F>
            <F label="Date Purchased"><Input type="date" value={form.date_purchased} onChange={e => set('date_purchased', e.target.value)} /></F>
            <F label="Purchase Price ($)"><Input type="number" step="0.01" value={form.purchase_price} onChange={e => set('purchase_price', e.target.value)} data-testid="eq-price" /></F>
            <F label="Current Replacement Cost ($)"><Input type="number" step="0.01" value={form.current_replacement_cost} onChange={e => set('current_replacement_cost', e.target.value)} data-testid="eq-repl-cost" /></F>
            <F label="Current Location"><Input value={form.current_location} onChange={e => set('current_location', e.target.value)} placeholder="Van #1, Sean..." /></F>
            <F label="Condition">
              <Select value={form.condition} onValueChange={v => set('condition', v)}>
                <SelectTrigger data-testid="eq-cond"><SelectValue /></SelectTrigger>
                <SelectContent>{CONDITIONS.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </F>
            <F label="Status">
              <Select value={form.status} onValueChange={v => set('status', v)}>
                <SelectTrigger data-testid="eq-stat"><SelectValue /></SelectTrigger>
                <SelectContent>{STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </F>
          </div>
          <F label="Notes"><Textarea rows={2} value={form.notes} onChange={e => set('notes', e.target.value)} /></F>
          <div className="flex justify-end gap-2 pt-2 border-t">
            <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Cancel</Button>
            <Button onClick={save} disabled={busy} className="bg-[#F56F05] hover:bg-[#e06505] text-white" data-testid="eq-save">
              {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              {equipment ? 'Save changes' : 'Add equipment'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
const F = ({ label, children }) => (<div className="space-y-1"><Label className="text-xs text-zinc-600">{label}</Label>{children}</div>);

export default AddEquipmentDialog;
